import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const VerifyEmailPage: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const { verifyEmail, loading, error } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleVerifyWithToken = useCallback(async (token: string) => {
    setIsVerifying(true);
    const success = await verifyEmail(token);
    if (success) {
      navigate('/login');
    }
    setIsVerifying(false);
  }, [verifyEmail, navigate]);

  useEffect(() => {
    // Check if token is in URL
    const token = searchParams.get('token');
    if (token) {
      handleVerifyWithToken(token);
    }
  }, [searchParams, handleVerifyWithToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length === 6) {
      await handleVerifyWithToken(verificationCode);
    }
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Lobster&display=swap"
        rel="stylesheet"
      />

      <div style={{
        minHeight: '100vh',
        background: theme === 'dark'
          ? '#1a1a1a'
          : 'rgba(177,187,214,0.60)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        position: 'relative'
      }}>
        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            background: 'none',
            border: 'none',
            borderRadius: '0.375rem',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            color: theme === 'dark' ? '#ffffff' : '#2d333a',
            transition: 'all 0.15s ease'
          }}
          title="Toggle theme"
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : '#f5f5f5';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>

      <div style={{
        width: '100%',
        maxWidth: '450px'
      }}>
        <div style={{
          backgroundColor: theme === 'dark' ? '#2d333a' : '#ffffff',
          borderRadius: '1rem',
          boxShadow: theme === 'dark'
            ? '0 20px 40px rgba(0, 0, 0, 0.3)'
            : '0 20px 40px rgba(0, 0, 0, 0.1)',
          padding: '3rem',
          border: `1px solid ${theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.2)'}`
        }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0fdf4',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              fontSize: '2rem'
            }}>
              📧
            </div>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 800,
              color: '#274797',
              marginBottom: '0.5rem',
              fontFamily: "'Lobster', cursive",
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}>
              SpaarxSense
            </h1>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: theme === 'dark' ? '#ffffff' : '#2d333a',
              marginBottom: '0.5rem'
            }}>
              Verify Your Email
            </h2>
            <p style={{
              color: theme === 'dark' ? '#9ca3af' : '#6e7681',
              fontSize: '1rem',
              marginBottom: '1rem'
            }}>
              Enter the 6-digit verification code
            </p>

            {/* Development Mode Notice */}
            <div style={{
              backgroundColor: theme === 'dark' ? '#1a3a1a' : '#f0f9ff',
              border: `1px solid ${theme === 'dark' ? '#2d5a2d' : '#bfdbfe'}`,
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '1.25rem' }}>�</span>
                <strong style={{
                  color: theme === 'dark' ? '#4ade80' : '#059669',
                  fontSize: '0.875rem'
                }}>
                  Check Your Email
                </strong>
              </div>
              <p style={{
                color: theme === 'dark' ? '#86efac' : '#047857',
                fontSize: '0.875rem',
                margin: 0
              }}>
                We've sent a 6-digit verification code to your email address. Check your inbox and enter the code below.
              </p>
            </div>
          </div>

          {!isVerifying ? (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: theme === 'dark' ? '#ffffff' : '#2d333a',
                  marginBottom: '0.5rem'
                }}>
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                    borderRadius: '0.5rem',
                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                    color: theme === 'dark' ? '#ffffff' : '#2d333a',
                    fontSize: '1.5rem',
                    textAlign: 'center',
                    letterSpacing: '0.5rem',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              {error && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '0.5rem',
                  color: '#dc2626',
                  fontSize: '0.875rem',
                  marginBottom: '1.5rem'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  border: 'none',
                  borderRadius: '0.5rem',
                  backgroundColor: loading || verificationCode.length !== 6 ? '#8e9297' : '#274797',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: loading || verificationCode.length !== 6 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  marginBottom: '1.5rem'
                }}
              >
                {loading ? (
                  <>
                    <div className="spinner" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </button>
            </form>
          ) : (
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}>
                <div className="spinner" />
                <span>Verifying your email...</span>
              </div>
            </div>
          )}

          <div style={{ textAlign: 'center' }}>
            <p style={{
              color: theme === 'dark' ? '#9ca3af' : '#6e7681',
              fontSize: '0.875rem',
              marginBottom: '1rem'
            }}>
              Didn't receive the code?
            </p>
            <button
              onClick={() => {/* Implement resend logic */}}
              style={{
                background: 'none',
                border: 'none',
                color: '#274797',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                marginBottom: '1rem'
              }}
            >
              Resend verification code
            </button>
            <br />
            <button
              onClick={() => navigate('/login')}
              style={{
                background: 'none',
                border: 'none',
                color: theme === 'dark' ? '#9ca3af' : '#6e7681',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default VerifyEmailPage;
